import momenttz from 'moment-timezone'
import UserSessionService from "./userSessionService"
import {i18n} from "../components/app/AppSetup"

/**
 * Utility methods for managing time with respect to the customer time zone
 * Note, the moment library will return browser local time NOT the customer time.
 * So use moment-timezone or the helper method below
 */
const TimeUtil = {
    /**
     * @param dateString   An ISO string.  Examples YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
     * @returns the moment object for the timestamp in the customer timezone
     */
    getDateInCustomerTimezone( dateString ) {
        let timezone = UserSessionService.getCustomerTimezone()

        // calling momenttz.tz(date, timezone) matters! The following lines will produce different outputs
        // with some time zones and DST
        //let wrongoutput = momenttz(dateString).tz(timezone).format('YYYY-MM-DD')
        //let output = momenttz.tz(dateString, timezone).format('YYYY-MM-DD')

        return momenttz.tz(dateString, timezone);
    },

    /**
     * @param truncateToStartOfDay - flag to truncate to day or leave all granularity
     * @returns the moment object for now in customer timezone
     */
    getNowInCustomerTimezone( truncateToStartOfDay = false) {
        let timezone = UserSessionService.getCustomerTimezone()

        if (truncateToStartOfDay) {
            return momenttz.tz(momenttz(), timezone).startOf('day')
        } else {
            return momenttz.tz(momenttz(), timezone);
        }
    },

    /**
     * @param t1 An ISO string.  Examples YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
     * @param t2 An ISO string.  Examples YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
     * @returns boolean, whether t1 occurs before t2
     */
    isBefore(t1, t2) { 
        let timezone = UserSessionService.getCustomerTimezone()

        return momenttz.tz(t1, timezone).isBefore(momenttz.tz(t2, timezone))
    },

    nmFormatDateWithTZ(params, format = 'MMM DD YYYY hh:mm a')
    {
        let value = params
        if (params instanceof Object)
        {
            value = params.value ? params.value : params.displayString
        }
        if (value) 
        {
            return TimeUtil.getDateInCustomerTimezone(value).format(format)
        }
        return null
    },
    nmFormatDateDisplayStringWithTZ(value, format = 'MMM DD YYYY hh:mm a')
    {
        if ((value) && (typeof value === 'object'))
        {
            if (value.displayString)
            {
                return TimeUtil.getDateInCustomerTimezone (value.displayString).format (format)
            }
        }
        else if (value)
        {
            return TimeUtil.getDateInCustomerTimezone(value).format(format)
        }
        return null
    },

    dateFormatter(params, format = 'MMM DD YYYY hh:mm a')
    {
        //for ag-grid columns where params is an object with a value property
        if (params && typeof params === 'object' && ('value' in params))
        {
            const fmt = params.colDef?.context?.fmt || format;
            const val = params.value
            if (val && typeof val === 'object')
            {
                if (val.displayString)
                {
                    return TimeUtil.getDateInCustomerTimezone(val.displayString).format(fmt)
                }
            }
            else if (val)
            {
                return TimeUtil.getDateInCustomerTimezone(val).format(fmt)
            }
            return ''
        }
        //for general use where params is directly a date string
        else if (params)
        {
            return TimeUtil.getDateInCustomerTimezone(params).format(format)
        }
        return ''
    },

    shortDateFormatter(params)
    {
        return TimeUtil.dateFormatter(params, 'MMM DD')
    },

    longDateFormatter(params)
    {
        return TimeUtil.dateFormatter(params, 'MMM DD YYYY hh:mm:ss a')
    },

    nmTowerStaleRenderer(params)
    {
        if (params?.value == null)
        {
            return null
        }
        let diff = TimeUtil.getNowInCustomerTimezone().diff(TimeUtil.getDateInCustomerTimezone(params.value), 'minute')
        if (diff >= 5)
        {
            return (TimeUtil.nmMinutesDuration(diff))
        }
        return (null)
    },

    historyIdFormatter(params)
    {
        //for ag-grid columns
        if (params?.value)
        {
            let dateObj = new Date(parseInt(params.value.substring(0, 8), 16) * 1000)
            return (TimeUtil.dateFormatter(dateObj.toISOString()))
        }
        else
        {
            return null
        }
    },
    nmMinutesDuration(value)
    {
        const MINUTES_PER_DAY = 1440
        const MINUTES_PER_HOUR = 60
        const SECONDS_PER_MINUTE = 60

        if (value !== undefined && value !== null)
        {
            let duration = ''
            let minutes

            // Handle value parameter being a scalar or an object with a value property, so that it can be used in table formatting
            if (typeof value === 'object')
            {
                minutes = value.value // e.g. params.value
            }
            else
            {
                minutes = value
            }
            if (minutes < 1)
            {
                return ((minutes*SECONDS_PER_MINUTE) + i18n.global.t('common.secondAbbrev'))
            }

            let days = Math.floor(minutes / MINUTES_PER_DAY)
            minutes = minutes % MINUTES_PER_DAY

            let hours = Math.floor(minutes / MINUTES_PER_HOUR)
            //javascript floating point math does not guarantee integer for % operator, so we will round if !== 0, omit if 0
            //we are omitting 0 to show tuning thresholds as 1day instead of 1day 0min, etc. it's a design choice.
            minutes = minutes % MINUTES_PER_HOUR ? (minutes % MINUTES_PER_HOUR).toFixed(0) : minutes % MINUTES_PER_HOUR

            if (days)
            {
                duration = days.toLocaleString() + i18n.global.t('common.dayAbbrev') + ' '
            }
            if (hours)
            {
                duration += hours + i18n.global.t('common.hourAbbrev') + ' '
            }
            if (minutes || !duration)
            {
                duration += minutes + i18n.global.t('common.minuteAbbrev')
            }
            return duration
        }
        return null
    },
    nmIsRadioStale(params, unit = 'days') {
    if (params?.value == null) {
        return false;
    }
    const threshold = unit === 'hours' ? 24 : 5;
    const diff = this.getNowInCustomerTimezone().diff(TimeUtil.getDateInCustomerTimezone(params.value), unit);
    return diff >= threshold;
    },
    /**
     *
     * Convert the sample interval from minutes to a readable string
     * @param value  sample interval in minutes
     * @returns {string}
     */
    nmDurationMinsFormatter(params) {
        if (!params || !params.value) {
            return ('')
        }
        let duration = Number (params.value)
        return (this.nmDurationSecsFormatter(duration*60))
    },
    nmDurationSecsFormatter(params) {
        if (params == null) {
            return ('')
        }
        let secs = params.value == null ? params : params.value
        let duration = Number (secs)
        if (isNaN(duration)) {
            return ('0 ' + i18n.global.t("common.seconds"))
        }
        if (duration >= 86400) {
            duration = Math.round (duration / 86400) + " days"
        } else if (duration >= 3660) {
            duration = Math.round (duration / 3660) + " hours"
        } else if (duration >= 60) {
            duration = Math.round (duration / 60) + " minutes"
        } else {
            duration = duration + " seconds"
        }
        return (duration)
    },
    parseTimeString (timeStr)
    {
        if (timeStr)
        {
            let values = timeStr.split('-')
            let time = values[1].slice (0, -1)
            let unit = values[1].slice (-1)
            return ({time: time, unit: unit})
        }
        return ({})
    },
    historyIdRenderer (params)
    {
        if (params.value)
        {
            let dateObj = new Date(parseInt(params.value.substring(0, 8), 16) * 1000)
            return (TimeUtil.nmFormatDateWithTZ(dateObj.toISOString()))
        }
        else
        {
            return null
        }
    },
}
export default TimeUtil
